import { IS_SSO_BRIDGE } from "constants/app.constants";

import { usePanelStore } from "stores/panelStore";
import styled from "styled-components";

import {
  IconLogo,
  IconLogo_AURORA,
  IconLogo_DEVA,
  IconLogo_GEVA,
  IconLogo_REVA,
  IconNoviWordmark
} from "components/icons";

/**
 * Height of the Novi wordmark, the vendor half of the lockup and the larger of
 * the two marks.
 */
const NOVI_MARK_HEIGHT = 20;

/**
 * Height of the EVA (or AURORA/DEVA/GEVA/REVA) wordmark *inside the lockup*.
 * Standalone, the product mark keeps its natural size and none of this applies.
 *
 * Smaller than the Novi mark on purpose: this is the same lockup every Novi
 * application wears, where the vendor wordmark leads and the product name sits
 * beside it at text weight. In the other apps that product half *is* text — "M&A
 * Intelligence" — so here the product wordmark is scaled to the size that text
 * would have been rather than competing with the Novi mark.
 *
 * Applied as a CSS height rather than through each icon's props because the
 * environment variants carry hardcoded `width`/`height` attributes; CSS beats a
 * presentational attribute, and the `viewBox` on each keeps the aspect ratio.
 */
const APP_MARK_HEIGHT = 14;

/**
 * Where each artwork's ink actually sits inside its own viewBox, as a fraction
 * of the box height. Measured from the rendered path geometry, not assumed.
 *
 * These differ sharply, which is the whole reason this file does arithmetic
 * instead of setting `align-items: center` and stopping. The Novi viewBox
 * reserves its top ~23% as headroom for the dot over the "i", so its
 * letterforms sit low in their own box. The product wordmarks very nearly fill
 * theirs. Centring the two *boxes* therefore centres nothing a reader can see:
 * it leaves the product mark floating ~2.4px above the Novi letterforms, which
 * is exactly the "EVA sits high" this corrects.
 *
 * The five product variants measure within 0.13px of each other at this size,
 * so one figure covers them all rather than five.
 */
const NOVI_INK = { top: 0.2313, bottom: 0.9952 };
const APP_INK = { top: 0.0019, bottom: 0.9846 };

/** Distance from a mark's box centre to its ink centre, positive downwards. */
const inkDrift = (ink: { top: number; bottom: number }, height: number) =>
  ((ink.top + ink.bottom) / 2) * height - height / 2;

/**
 * The nudge that puts the product wordmark's optical centre on the Novi
 * wordmark's, so "EVA" reads as centred against "novi" rather than against the
 * empty space above it.
 */
const APP_MARK_OFFSET =
  inkDrift(NOVI_INK, NOVI_MARK_HEIGHT) - inkDrift(APP_INK, APP_MARK_HEIGHT);

const Logo = () => {
  const closePanel = usePanelStore((state) => state.closePanel);

  const closeSettings = () => {
    closePanel();
  };

  const getAppLogo = () => {
    switch (process.env.REACT_APP_ENV) {
      case "mcdan":
        return <IconLogo_AURORA />;
      case "research":
        return <IconLogo_REVA />;
      case "gather":
        return <IconLogo_GEVA />;
      case "dev":
      case "dev-aws":
        return <IconLogo_DEVA />;
      default:
        return <IconLogo />;
    }
  };

  /*
   * The Novi lockup is bridge-only. Outside the bridge there is no app switcher
   * and no sibling applications to reach, so the vendor wordmark would be
   * naming a suite the user cannot see from here — and it would cost the
   * product mark a third of its height to say it. There, the product mark
   * stands alone at its natural size, exactly as it did before the lockup.
   *
   * Read through the module rather than captured at import so a test can toggle
   * it; Vite inlines it either way in a build.
   */
  if (!IS_SSO_BRIDGE) {
    return <Wrapper onClick={() => closeSettings()}>{getAppLogo()}</Wrapper>;
  }

  return (
    <Wrapper onClick={() => closeSettings()}>
      <IconNoviWordmark size={NOVI_MARK_HEIGHT} />
      <AppMark>{getAppLogo()}</AppMark>
    </Wrapper>
  );
};

export default Logo;

// Serves both shapes: with one child the gap and alignment are inert, so the
// standalone product mark renders exactly as it did before the lockup existed.
//
// 8px is novi-apps' `.brand` gap, and it lands on the same optical gap here
// (8.05px measured) rather than a merely equal number: the Novi wordmark's ink
// runs to within 0.05px of its box edge and the product artwork starts flush at
// its own, so nothing is padding this from either side.
const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text);
`;

// Sizes whichever environment variant rendered: each is a differently-sized svg
// with a viewBox, so constraining height and freeing width scales them all to a
// common cap line without hardcoding five widths. The translate is the optical
// centring described on APP_MARK_OFFSET.
const AppMark = styled.span`
  display: inline-flex;
  align-items: center;
  transform: translateY(${APP_MARK_OFFSET}px);

  svg {
    display: block;
    height: ${APP_MARK_HEIGHT}px;
    width: auto;
  }
`;
